import { Button, ButtonProps } from '@mantine/core';

import { Subscription, SubscriptionFromSubscriptionType, SubscriptionType } from 'models';
import { SubscriptionConfigHelpers } from 'helpers/SubscriptionConfigHelpers';
import { SubscriptionHelpers } from 'helpers/SubscriptionHelpers';

import { useCurrentTeam } from 'hooks/use-current-team';
import { SubscriptionModalService } from 'services/SubscriptionModalService';

import { SubscriptionUpdateCommonPropsWithoutDefaults } from '../types';

export const SubscriptionButton = <T extends SubscriptionType>({
    type,
    toSubscription,
    buttonProps = {},
    buttonLabel: buttonLabelOverride,
    ...subscriptionProps
}: {
    type: T;
    toSubscription: SubscriptionFromSubscriptionType<T>;
    buttonProps?: ButtonProps;
    buttonLabel?: string;
} & SubscriptionUpdateCommonPropsWithoutDefaults) => {
    const currentTeam = useCurrentTeam();
    const teamToUpgrade = subscriptionProps.team ?? currentTeam ?? undefined;

    const currentSubscription = SubscriptionHelpers.getSubscriptionData(
        type,
        teamToUpgrade?.subscriptions,
    ).subscription;

    const isFree = SubscriptionHelpers.isFreeSubscription(toSubscription);

    const isDowngrade = SubscriptionConfigHelpers.isDowngrade(type, currentSubscription, toSubscription);

    const isCurrentSubscription = currentSubscription === toSubscription;

    if (isFree && isCurrentSubscription) {
        return (
            <Button fullWidth variant="filled" disabled size="md" {...buttonProps}>
                Current Subscription
            </Button>
        );
    }

    const disabled = isCurrentSubscription && type !== SubscriptionType.CREDIT;
    const buttonLabel = getButtonLabel({ isCurrentSubscription, isDowngrade, toSubscription });

    return (
        <Button
            onClick={() => SubscriptionModalService.open({ type, toSubscription, ...subscriptionProps })}
            fullWidth
            variant={isDowngrade || isFree ? 'default' : 'filled'}
            disabled={disabled}
            size="md"
            {...buttonProps}
        >
            {buttonLabelOverride || buttonLabel}
        </Button>
    );
};

const getButtonLabel = ({
    isCurrentSubscription,
    isDowngrade,
    toSubscription,
}: {
    isCurrentSubscription: boolean;
    isDowngrade: boolean;
    toSubscription: Subscription;
}) => {
    if (isCurrentSubscription) {
        if (SubscriptionHelpers.isSubscriptionType(SubscriptionType.CREDIT, toSubscription)) {
            return 'Add or remove credits';
        }

        return 'Current plan';
    }

    return isDowngrade ? 'Change plan' : 'Upgrade';
};
